import { clsx } from 'clsx'
import type { StandingsEntry } from '../types'
import styles from './LeagueStandingHero.module.css'

export type HeroGapStat = { label: string; value: string }

type LeagueStandingHeroProps = {
  entry: StandingsEntry | null
  totalParticipants: number
  gap: HeroGapStat | null
  isH2H: boolean
  windowCountdown: string | null
  onEditSquad: () => void
}

function Stat({ k, v, accent = false }: { k: string; v: React.ReactNode; accent?: boolean }) {
  return (
    <div className={styles.stat}>
      <span className={styles.statK}>{k}</span>
      <span className={clsx(styles.statV, accent && styles.statVAccent)}>{v}</span>
    </div>
  )
}

export function LeagueStandingHero({
  entry,
  totalParticipants,
  gap,
  isH2H,
  windowCountdown,
  onEditSquad,
}: LeagueStandingHeroProps) {
  const hasSquad = entry?.hasSquad ?? false

  // No squad yet (or no standing entry) → onboarding CTA instead of an empty rank.
  if (!entry || !hasSquad) {
    return (
      <div className={clsx(styles.hero, styles.heroEmpty)}>
        <div className={styles.emptyIcon} aria-hidden="true">⚽</div>
        <div className={styles.emptyText}>
          <div className={styles.emptyTitle}>Monte sua escalação</div>
          <p className={styles.emptySub}>
            {windowCountdown
              ? `Você ainda não escalou seu time. A janela fecha em ${windowCountdown}.`
              : 'Você ainda não montou seu time para esta liga.'}
          </p>
        </div>
        <button className={styles.cta} onClick={onEditSquad}>Montar agora →</button>
      </div>
    )
  }

  const delta = entry.previousRank === null ? null : entry.previousRank - entry.rank
  const moveClass =
    delta === null || delta === 0 ? styles.moveFlat : delta > 0 ? styles.moveUp : styles.moveDown
  const moveText =
    delta === null
      ? '— estreia nesta rodada'
      : delta === 0
        ? '— sem mudança de posição'
        : delta > 0
          ? `▲ subiu ${delta} ${delta === 1 ? 'posição' : 'posições'}`
          : `▼ caiu ${Math.abs(delta)} ${Math.abs(delta) === 1 ? 'posição' : 'posições'}`

  return (
    <div className={styles.hero}>
      <div className={styles.rankWrap}>
        <div className={styles.rank}>{entry.rank}</div>
        <div className={styles.rankSub}>de {totalParticipants}</div>
      </div>

      <div className={styles.mid}>
        <div className={styles.team}>{entry.teamName}</div>
        <span className={clsx(styles.move, moveClass)}>{moveText}</span>
        <div className={styles.stats}>
          {isH2H && entry.h2hPoints !== undefined && (
            <Stat k="Pts H2H" v={entry.h2hPoints} accent />
          )}
          <Stat k="Pts rodada" v={entry.gwPoints} accent={!isH2H} />
          <Stat k="Pts total" v={entry.totalPoints.toLocaleString('pt-BR')} />
          <Stat k="Transfer." v={entry.transfersUsed} />
          {gap && <Stat k={gap.label} v={gap.value} />}
        </div>
      </div>

      <button className={styles.cta} onClick={onEditSquad}>Editar escalação →</button>
    </div>
  )
}
